import React, { useCallback, useEffect, useRef } from 'react';

import { Textarea } from '@/shared/components/shadcn/textarea';
import { cn } from '@/shared/utils/cn';

interface Props extends React.ComponentProps<'textarea'> {
  /**
   * Maximum height in pixels for the textarea
   * @default 120
   */
  maxHeight?: number;
  /**
   * Minimum height in pixels for the textarea
   * @default 40
   */
  minHeight?: number;
  ref?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Textarea component that automatically resizes based on content
 */
export const AutoResizeTextarea = ({
  className,
  maxHeight = 120,
  minHeight = 40,
  value,
  ref,
  ...props
}: Props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height within bounds
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    // Apply the new height
    textarea.style.height = `${newHeight}px`;
  }, [maxHeight, minHeight]);

  // Auto-resize when value changes
  useEffect(() => {
    resize();
  }, [value, resize]);

  // Auto-resize on mount
  useEffect(() => {
    resize();
  }, [resize]);

  return (
    <Textarea
      ref={(element) => {
        // Handle both refs
        textareaRef.current = element;
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
      value={value}
      className={cn('resize-none overflow-y-auto', className)}
      style={{
        height: 'auto',
        maxHeight: `${maxHeight}px`,
        minHeight: `${minHeight}px`,
      }}
      onInput={resize}
      {...props}
    />
  );
};

AutoResizeTextarea.displayName = 'AutoResizeTextarea';
